import {
  Box,
  VStack,
  HStack,
  Avatar,
  Text,
  Divider,
  Switch,
  Spinner,
  Icon,
  Flex,
  Badge,
  Button,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Pencil,
  Globe,
  Mail,
  MapPin,
  Phone,
  Info,
  Bot,
  Heading,
} from 'lucide-react';
import { deleteWANumber, getWANumber } from '../../../hooks/whatsapp';
import { updateAIChatResponses } from '../../../hooks/whatsapp_number';
import { WhatsappNumber } from '../../../utils/types';
import DisconnectChannelDialog from '../../../components/DisconnectChannelDialog';

const WA_GREEN = '#009800ff';

export default function WhatsAppBusinessProfilePage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [wa, setWA] = useState<WhatsappNumber | null>(null);
  const [updatingAi, setUpdatingAi] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    (async () => {
      const res = await getWANumber(orgId);
      console.log('Fetched WA number:', res);
      if (res?.success) setWA(res.data[0]);
      setLoading(false);
    })();
  }, [orgId]);

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center">
        <Spinner />
      </Flex>
    );
  }

  if (!wa) return null;

  const handleToggleAiReplies = async () => {
    if (!orgId || updatingAi) {
      return;
    }

    const previousValue = wa.ai_chat_responses;
    const nextValue = !previousValue;
    setUpdatingAi(true);
    setWA({ ...wa, ai_chat_responses: nextValue });

    const response = await updateAIChatResponses(wa.id, orgId, nextValue);
    setUpdatingAi(false);

    if (!response.success) {
      setWA({ ...wa, ai_chat_responses: previousValue });
      toast({
        title: 'AI auto replies were not updated',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: nextValue ? 'AI auto replies enabled' : 'AI auto replies disabled',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleDisconnect = async () => {
    if (!orgId || !wa?.id) {
      return;
    }

    setDisconnecting(true);
    const response = await deleteWANumber(orgId, wa.id);
    setDisconnecting(false);

    if (!response.success) {
      toast({
        title: 'WhatsApp disconnect failed',
        description: 'The WhatsApp Business account could not be disconnected.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    onClose();
    toast({
      title: 'WhatsApp disconnected',
      description: 'The WhatsApp Business account has been fully disconnected.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    navigate(`/organization/${orgId}/inbox/whatsapp/conversations`);
  };

  return (
    <Box bg="white" minH="100vh" maxW={'600px'} paddingX={3} paddingBottom={5}>
      {/* Profile Header */}
      <VStack spacing={3} py={6}>
        <Avatar
          size="2xl"
          name={wa.display_name || 'Business'}
          bg={WA_GREEN}
          color="white"
        />
        <Text fontSize="lg" fontWeight="semibold">
          {wa.display_name}
        </Text>
        <Text color="gray.500">WhatsApp Business Account</Text>
      </VStack>

      <Divider />

      {/* Business Info */}
      <VStack spacing={0} align="stretch">
        <ProfileRow
          icon={Info}
          label="About"
          value={wa.about || 'No description'}
          link={`/organization/${orgId}/inbox/whatsapp/${wa.id}/settings/edit-about`}
        />
        <ProfileRow
          icon={Phone}
          label="Phone"
          value={wa.phone_number || 'Not available'}
          link={`/organization/${orgId}/inbox/whatsapp/${wa.id}/settings/edit-phone-number`}
        />
        <ProfileRow
          icon={Mail}
          label="Email"
          value={wa.email || 'Not available'}
          link={`/organization/${orgId}/inbox/whatsapp/${wa.id}/settings/edit-email`}
        />
        <ProfileRow
          icon={Globe}
          label="Website"
          value={wa.website || 'Not available'}
          link={`/organization/${orgId}/inbox/whatsapp/${wa.id}/settings/edit-website`}
        />
        <ProfileRow
          icon={MapPin}
          label="Address"
          value={wa.address || 'Not available'}
          link={`/organization/${orgId}/inbox/whatsapp/${wa.id}/settings/edit-address`}
        />
      </VStack>

      <Divider />

      {/* Automations */}
      <VStack spacing={0} align="stretch">
        <HStack px={4} py={3} justify="space-between">
          <HStack>
            <Icon as={Bot} />
            <Text fontSize="sm">AI Auto Replies</Text>
          </HStack>
          <Switch
            isChecked={wa.ai_chat_responses}
            colorScheme="green"
            isDisabled={updatingAi}
            onChange={handleToggleAiReplies}
          />
        </HStack>
      </VStack>

      <Divider />

      {/* Status */}
      <HStack px={4} py={3} justify="space-between">
        <Text fontSize="sm">Account Status {wa.status}</Text>
        <Badge colorScheme={wa.status === 'active' ? 'green' : 'red'}>
          {wa.status === 'active' ? 'Active' : 'Inactive'}
        </Badge>
      </HStack>

      <Divider />

      <Box px={4} py={4}>
        <Button colorScheme="red" borderRadius="full" onClick={onOpen} isLoading={disconnecting}>
          Disconnect WhatsApp account
        </Button>
      </Box>

      <DisconnectChannelDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleDisconnect}
        title="Disconnect WhatsApp account?"
        body={`This will disconnect ${wa.display_name || wa.phone_number || 'this WhatsApp account'} from this organization and stop new WhatsApp sync.`}
        confirmLabel="Disconnect WhatsApp"
        isLoading={disconnecting}
      />
    </Box>
  );
}

function ProfileRow({
  icon,
  label,
  value,
  link,
}: {
  icon: any;
  label: string;
  value: string;
  link: string;
}) {
  const navigate = useNavigate();

  return (
    <>
      <HStack px={4} py={3} spacing={4} justifyContent={'space-between'}>
        <HStack>
          <Icon as={icon} />
          <Box flex={1}>
            <Text fontSize="xs" color="gray.500">
              {label}
            </Text>
            <Text fontSize="sm">{value}</Text>
          </Box>
        </HStack>
        <Icon
          as={Pencil}
          size={18}
          alignSelf={'flex-end'}
          color="gray"
          onClick={() => navigate(link)}
        />
      </HStack>
      <Divider />
    </>
  );
}
